import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule } from '@ngrx/effects';
import { ClipboardModule, ClipboardService } from 'ngx-clipboard';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';

import { OverlayTransitionComponent } from 'app/commons/overlay-transition/overlay-transition.component';
import { formatUserNamePipe } from 'app/core/utilities/formatUserName.pipe';
import { SafeHtmlPipe } from 'app/core/utilities/safe-html.pipe';

import { CommonsModule } from '../../commons/commons.module';
import { CoreModule } from '../../core/core.module';
import { ModalAddLivestreamComponent } from '../../shared/modals/add-livestream-modal/add-livestream-modal.component';
import { SharedModule } from '../../shared/shared.module';
import { AdminService } from '../admin/services/admin.service';
import { SigninModule } from '../signin/signin.module';
import { SurveyModule } from '../survey/survey.module';

import { AlternateHostsSelectorComponent } from './alternate-hosts-selector/alternate-hosts-selector.component';
import { BadgingInfoComponent } from './badging-info/badging-info.component';
import { CameraComponent } from './camera/camera.component';
import { SocialChatComponent } from './chat/social-chat/social-chat.component';
import { SocialChatComposerComponent } from './chat/social-chat-composer/social-chat-composer.component';
import { ChatFeedComponent } from './chat/social-chat-feed/chat-feed/chat-feed.component';
import { HostChatFeedComponent } from './chat/social-chat-feed/host-chat-feed/host-chat-feed.component';
import { ModerationChatFeedComponent } from './chat/social-chat-feed/moderation-chat-feed/moderation-chat-feed.component';
import { SocialChatFeedComponent } from './chat/social-chat-feed/social-chat-feed.component';
import { SocialChatSearchComponent } from './chat/social-chat-search/social-chat-search.component';
import { ClosedCaptionsComponent } from './closed-captions/closed-captions.component';
import { ConnectedRoomsSelectOptionComponent } from './connected-rooms-select-option/connected-rooms-select-option.component';
import { NetworkDebuggerComponent } from './debugger/debugger-network/network-debugger.component';
import { PeerConnectionDebuggerComponent } from './debugger/debugger-peer-connections/peer-connection-debugger';
import { DebuggerScreenshareComponent } from './debugger/debugger-screenshare/debugger-screenshare.component';
import { SoundDebuggerComponent } from './debugger/debugger-sound/sound-debugger.component';
import { DebuggerSubscriptionsComponent } from './debugger/debugger-subscriptions/debugger-subscriptions.component';
import { DebuggerComponent } from './debugger/debugger.component';
import { FeatureFlagDebuggerComponent } from './debugger/feature-flag-debugger/feature-flag-debugger.component';
import { MediaDebuggerComponent } from './debugger/media-debugger/media-debugger.component';
import { PerformanceDebuggerComponent } from './debugger/performance-debugger/performance-debugger.component';
import { RoomSettingsDebuggerComponent } from './debugger/room-settings-debugger/room-settings-debugger.component';
import { SceneDebuggerComponent } from './debugger/scene-debugger/scene-debugger.component';
import { TestUtilitiesComponent } from './debugger/test-utilities/test-utilities.component';
import { GroupShareRecordingBannerComponent } from './group-share-recording-banner/group-share-recording-banner.component';
import { GuestPageComponent } from './guest-page/guest-page.component';
import { HeaderToastMessageComponent } from './header-toast-message/header-toast-message.component';
import { HelpModalComponent } from './help-modal/help-modal.component';
import { JoinSocialHomeComponent } from './join-social-home/join-social-home.component';
import { ModalAccessComponent } from './modal-access/modal-access.component';
import { ModalBadgingQuestionsComponent } from './modal-badging-questions/modal-badging-questions.component';
import { ModalConfirmDeleteComponent } from './modal-confirm-delete/modal-confirm-delete.component';
import { ModalConvoComponent } from './modal-convo/modal-convo.component';
import { ModalCreateSocialComponent } from './modal-create-social/modal-create-social.component';
import { ModalDeleteSocialComponent } from './modal-delete-social/modal-delete-social.component';
import { ModalDialogComponent } from './modal-dialog/modal-dialog.component';
import { ModalInputSocialComponent } from './modal-input-social/modal-input-social.component';
import { ModalMusicPlayListComponent } from './modal-music-playlist/modal-music-playlist.component';
import { ModalNameComponent } from './modal-name/modal-name.component';
import { ModalRecordingNameComponent } from './modal-recording-name/modal-recording-name.component';
import { ModalSimpleSocialComponent } from './modal-simple-social/modal-simple-social.component';
import { ModalSocialEventComponent } from './modal-social-event/modal-social-event.component';
import { ModalSocialInviteComponent } from './modal-social-invite/modal-social-invite.component';
import { ModalSocialSignUpComponent } from './modal-social-sign-up/modal-social-sign-up.component';
import { ModalVideoEffectsComponent } from './modal-video-effects/modal-video-effects.component';
import { MultimediaDropdownComponent } from './multimedia-dropdown/multimedia-dropdown.component';
import { NavigationModalComponent } from './navigation-modal/navigation-modal.component';
import { OnboardingExperienceComponent } from './onboarding-experience/onboarding-experience.component';
import { PaginationComponent } from './pagination/pagination.component';
import { PhotoBoothComponent } from './photo-booth/photo-booth.component';
import { PhotoBoothViewerComponent } from './photo-booth-viewer/photo-booth-viewer.component';
import { PixiOnboardingExperienceComponent } from './pixi-onboarding-experience/pixi-onboarding-experience.component';
import { EmoteStreamComponent } from './presentation/emote-stream/emote-stream.component';
import { PresentationGalleryComponent } from './presentation/presentation-drawer/gallery/gallery.component';
import { PresentationDrawerComponent } from './presentation/presentation-drawer/presentation-drawer.component';
import { PresentationGroupShareComponent } from './presentation/presentation-group-share/presentation-group-share.component';
import { PresentationHeaderComponent } from './presentation/presentation-header/presentation-header.component';
import { PresentationPopoutButtonComponent } from './presentation/presentation-popout-button/presentation-popout-button.component';
import { PresentationPresenterComponent } from './presentation/presentation-presenter/presentation-presenter.component';
import { PresentationPresenterEmbedComponent } from './presentation/presentation-presenter-embed/presentation-presenter-embed.component';
import { PresentationPresenterSharingComponent } from './presentation/presentation-presenter-sharing/presentation-presenter-sharing.component';
import { PresentationPresenterVideosComponent } from './presentation/presentation-presenter-videos/presentation-presenter-videos.component';
import { PresentationShellComponent } from './presentation/presentation-shell/presentation-shell.component';
import { PresentationSpeakersComponent } from './presentation/presentation-speakers/presentation-speakers.component';
import { PresentationSpeakersDirective } from './presentation/presentation-speakers/presentation-speakers.directive';
import { PresentationThumbprintComponent } from './presentation/presentation-thumbprint/presentation-thumbprint.component';
import { PresentationMenuDropdownComponent } from './presentation/presentation-toolbar/presentation-menu-dropdown/presentation-menu-dropdown.component';
import { PresentationSoundIconComponent } from './presentation/presentation-toolbar/presentation-sound-icon/presentation-sound-icon.component';
import { PresentationToolBarComponent } from './presentation/presentation-toolbar/presentation-toolbar.component';
import { SearchParticipantsComponent } from './presentation/presentation-toolbar/search-participants/search-participants.component';
import { PresentationModalComponent } from './presentation-modal/presentation-modal.component';
import { QAAcceptRaisedHandsComponent } from './qa-accept-raised-hands/qa-accept-raised-hands.component';
import { QARaiseHandComponent } from './qa-raise-hand/qa-raise-hand.component';
import { RecordingBannerComponent } from './recording-banner/recording-banner.component';
import { RightCollapsibleSectionComponent } from './right-collapsible-section/right-collapsible-section.component';
import { RingColorsLegendComponent } from './ring-colors-legend/ring-colors-legend.component';
import { RoomTutorialComponent } from './room-tutorial/room-tutorial.component';
import { ActionMenuService } from './services/action-menu.service';
import { ArtifactService } from './services/artifact/artifact.service';
import { BadgingService } from './services/badging/badging.service';
import { BrowserService } from './services/browser.service';
import { ChatService } from './services/chat.service';
import { CleanUpTasksService } from './services/clean-up-tasks.service';
import { ClipboardResponseService } from './services/clipboard-response.service';
import { ClosedCaptionsService } from './services/closed-captions/closed-captions.service';
import { ConnectedRoomsService } from './services/connected-rooms.service';
import { ConvoParticipantService } from './services/convo-participant.service';
import { ConvoService } from './services/convo.service';
import { CTAService } from './services/cta/cta.service';
import { DebuggerService } from './services/debugger.service';
import { DeviceService } from './services/device/device.service';
import { DevicePermissionService } from './services/device-permission/device-permission.service';
import { EasingService } from './services/easing-service';
import { EntryFlowService } from './services/entry-flow.service';
import { GalleryService } from './services/gallery/gallery.service';
import { HeaderMultipleOptionsMessageService } from './services/header-multiple-options-message.service';
import { HeaderToastMessageService } from './services/header-toast-message.service';
import { HostService } from './services/host-service';
import { LivestreamService } from './services/livestream/livestream.service';
import { ModalSocialEventService } from './services/modal-social-event.service';
import { MonitoringService } from './services/monitor/monitoring.service';
import { MultimediaRoomService } from './services/multimedia-room.service';
import { MultimediaUserService } from './services/multimedia-user.service';
import { PerformerSelectorService } from './services/performer-selector.service';
import { PerformerViewService } from './services/performer-view.service';
import { PerformerService } from './services/performer.service';
import { PermissionsService } from './services/permissions.service';
import { PhotoBoothService } from './services/photo-booth.service';
import { PlaylistService } from './services/playlist.service';
import { CrossRoomParticipantService } from './services/presentation/cross-room-participant.service';
import { PresentationAudioService } from './services/presentation/presentation-audio.service';
import { PresentationRecorderService } from './services/presentation/presentation-recorder.service';
import { PresentationService } from './services/presentation/presentation.service';
import { QAService } from './services/qa.service';
import { RoomLifecycleService } from './services/room/room-lifecycle.service';
import { RoomPresenceService } from './services/room/room-presence.service';
import { RoomStateService } from './services/room/room-state.service';
import { RtcStatsService } from './services/rtc-stats.service';
import { SceneService } from './services/scene.service';
import { ShareScreenParticipantService } from './services/share-screen/share-screen-participant.service';
import { ShareScreenService } from './services/share-screen/share-screen.service';
import { GroupShareScreenParticipantService } from './services/share-screen-group/group-share-screen-participant.service';
import { GroupShareScreenRecorderService } from './services/share-screen-group/group-share-screen-recorder.service';
import { GroupShareScreenService } from './services/share-screen-group/group-share-screen.service';
import { SocialMessageService } from './services/social-message.service';
import { SocialResolverService } from './services/social-resolver.service';
import { SubscriptionService } from './services/subscription/subscription.service';
import { ThemeService } from './services/theme.service';
import { TimerService } from './services/timer.service';
import { ToastService } from './services/toast/toast.service';
import { ToasterVideoService } from './services/toaster-video.service';
import { BlackSimpleToasterService } from './services/toasters/black-simple-toaster.service';
import { TransitionPresentationService } from './services/transition-presentation.service';
import { TransitionService } from './services/transition.service';
import { VideoEffectService } from './services/video-effect.service';
import { VideoRoomService } from './services/video-room.service';
import { VideoChatService } from './services/videochat.service';
import { WebsocketTimerService } from './services/websocket-timer.service';
import { SocialHeaderComponent } from './social-header/social-header.component';
import { SocialLeaveRoomComponent } from './social-leave-room/social-leave-room.component';
import { ActionMenuComponent } from './social-menu/action-menu/action-menu.component';
import { SubmenuIconComponent } from './social-menu/action-menu/components/submenu-icon.component';
import { AdvancedPresentationSettingsComponent } from './social-menu/advanced-presentation-settings/advanced-presentation-settings.component';
import { AudioSettingsComponent } from './social-menu/audio-settings/audio-settings.component';
import { FileLibraryComponent } from './social-menu/file-library/file-library.component';
import { GuestAdmissionComponent } from './social-menu/guest-admission/guest-admission.component';
import { GuestInfoComponent } from './social-menu/guest-info/guest-info.component';
import { GuestsListComponent } from './social-menu/guests-list/guests-list.component';
import { ManagePresentersComponent } from './social-menu/manage-presenters/manage-presenters.component';
import { PresentationSettingsComponent } from './social-menu/presentation-settings/presentation-settings.component';
import { SocialMenuHeaderComponent } from './social-menu/social-menu-header/social-menu-header.component';
import { SocialMenuComponent } from './social-menu/social-menu.component';
import { SocialMiniMapComponent } from './social-mini-map/social-mini-map.component';
import { SavedRoomsDialogComponent } from './social-page/components/saved-rooms-dialog/saved-rooms-dialog.component';
import { SavedRoomsTableComponent } from './social-page/components/saved-rooms-table/saved-rooms-table.component';
import { SocialPageService } from './social-page/services/social-page.service';
import { NativeCursorDirective } from './social-pixi-canvas/native-cursor.directive';
import { SocialPixiCanvasComponent } from './social-pixi-canvas/social-pixi-canvas.component';
import { SocialRoutingModule } from './social-routing.module';
import { SocialThemeCarouselComponent } from './social-theme-carousel/social-theme-carousel.component';
import { SocialAppEffects } from './state/app/effects';
import { SocialHomePageEffects } from './state/home-page/effects';
import { TimerBannerComponent } from './timer-banner/timer-banner.component';
import { ToastBannerComponent } from './toast-banner/toast-banner.component';
import { ToastBannerMessageComponent } from './toast-banner-message/toast-banner-message.component';
import { VideoBubbleComponent } from './video-bubble/video-bubble.component';
import { WaitingRoomComponent } from './waiting-room/waiting-room.component';
import { WarningBannerComponent } from './warning-banner/warning-banner.component';
import { BadgingQuestionsComponent } from './welcome/badging-questions/badging-questions.component';
import { WelcomeDevicesFormComponent } from './welcome/welcome-devices-form/welcome-devices-form.component';
import { WelcomePreviewComponent } from './welcome/welcome-preview/welcome-preview.component';
import { WelcomeRoomTransitionComponent } from './welcome/welcome-room-transition/welcome-room-transition.component';
import { WelcomeWrapperComponent } from './welcome/welcome-wrapper/welcome-wrapper.component';
import { ZoomPixiWorldControlsComponent } from './zoom-pixi-world-controls/zoom-pixi-world-controls.component';
import { TimeDiffPipe } from '../../commons/pipes/time-diff.pipe';
import { ModalSpecialAccessComponent } from './modal-special-access/modal-special-access.component';
import { EventCountdownPageComponent } from '../social-events/pages/event-countdown/event-countdown-page.component';
import { SagDebuggerComponent } from './debugger/sag-debugger/sag-debugger.component';
import { TimeDiffAsyncPipe } from 'app/commons/pipes/time-diff-async.pipe';

@NgModule({
  declarations: [
    ActionMenuComponent,
    AdvancedPresentationSettingsComponent,
    CameraComponent,
    ConnectedRoomsSelectOptionComponent,
    GuestAdmissionComponent,
    GuestInfoComponent,
    GuestPageComponent,
    GuestsListComponent,
    HeaderToastMessageComponent,
    JoinSocialHomeComponent,
    ModalAccessComponent,
    ModalAddLivestreamComponent,
    ModalConvoComponent,
    ModalCreateSocialComponent,
    ModalDialogComponent,
    ModalDeleteSocialComponent,
    ModalInputSocialComponent,
    ModalMusicPlayListComponent,
    ModalNameComponent,
    ModalSimpleSocialComponent,
    ModalSocialEventComponent,
    ModalSocialInviteComponent,
    ModalSocialSignUpComponent,
    ModalRecordingNameComponent,
    ModalVideoEffectsComponent,
    MultimediaDropdownComponent,
    NavigationModalComponent,
    PhotoBoothComponent,
    PhotoBoothViewerComponent,
    AudioSettingsComponent,
    QAAcceptRaisedHandsComponent,
    QARaiseHandComponent,
    RightCollapsibleSectionComponent,
    SavedRoomsDialogComponent,
    SavedRoomsTableComponent,
    SocialChatComponent,
    SocialChatComposerComponent,
    SocialChatFeedComponent,
    SocialChatSearchComponent,
    SocialHeaderComponent,
    SocialLeaveRoomComponent,
    SocialMenuComponent,
    SocialMenuHeaderComponent,
    SubmenuIconComponent,
    SocialMiniMapComponent,
    SocialPixiCanvasComponent,
    SocialThemeCarouselComponent,
    TimerBannerComponent,
    ToastBannerComponent,
    ToastBannerMessageComponent,
    VideoBubbleComponent,
    WaitingRoomComponent,
    WarningBannerComponent,
    ZoomPixiWorldControlsComponent,
    DebuggerComponent,
    DebuggerSubscriptionsComponent,
    PerformanceDebuggerComponent,
    NetworkDebuggerComponent,
    MediaDebuggerComponent,
    PresentationSettingsComponent,
    ManagePresentersComponent,
    RecordingBannerComponent,
    SoundDebuggerComponent,
    FeatureFlagDebuggerComponent,
    SceneDebuggerComponent,
    PeerConnectionDebuggerComponent,
    DebuggerScreenshareComponent,
    RoomSettingsDebuggerComponent,
    SafeHtmlPipe,
    formatUserNamePipe,
    RoomTutorialComponent,
    HelpModalComponent,
    AlternateHostsSelectorComponent,
    TestUtilitiesComponent,
    GroupShareRecordingBannerComponent,
    BadgingInfoComponent,
    PresentationToolBarComponent,
    PresentationSoundIconComponent,
    PresentationDrawerComponent,
    PresentationModalComponent,
    PresentationGalleryComponent,
    PresentationMenuDropdownComponent,
    PresentationGroupShareComponent,
    OverlayTransitionComponent,
    PresentationThumbprintComponent,
    PresentationPresenterComponent,
    EmoteStreamComponent,
    PresentationPresenterVideosComponent,
    PresentationPresenterSharingComponent,
    PresentationPresenterEmbedComponent,
    PresentationShellComponent,
    PresentationHeaderComponent,
    PresentationSpeakersComponent,
    PresentationSpeakersDirective,
    WelcomeWrapperComponent,
    WelcomePreviewComponent,
    WelcomeDevicesFormComponent,
    BadgingQuestionsComponent,
    SearchParticipantsComponent,
    PaginationComponent,
    PresentationPopoutButtonComponent,
    WelcomeRoomTransitionComponent,
    RingColorsLegendComponent,
    ClosedCaptionsComponent,
    NativeCursorDirective,
    ModalBadgingQuestionsComponent,
    FileLibraryComponent,
    ChatFeedComponent,
    HostChatFeedComponent,
    ModerationChatFeedComponent,
    ModalConfirmDeleteComponent,
    OnboardingExperienceComponent,
    PixiOnboardingExperienceComponent,
    ModalSpecialAccessComponent,
    EventCountdownPageComponent,
    SagDebuggerComponent,
  ],
  imports: [
    CommonModule,
    CoreModule,
    CommonsModule,
    SharedModule,
    FormsModule,
    SocialRoutingModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatButtonModule,
    MatDialogModule,
    MatGridListModule,
    MatBadgeModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatCheckboxModule,
    MatDividerModule,
    MatTooltipModule,
    MatSliderModule,
    MatSlideToggleModule,
    ClipboardModule,
    RouterModule,
    ReactiveFormsModule,
    NgbModule,
    SurveyModule,
    SigninModule,
    SharedModule,
    MatRadioModule,
    MatRippleModule,
    MatExpansionModule,
    EffectsModule.forFeature([SocialHomePageEffects, SocialAppEffects]),
    InfiniteScrollModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    DragDropModule,
    PickerComponent,
    TimeDiffPipe,
    TimeDiffAsyncPipe,
  ],
  providers: [
    PerformerSelectorService,
    DeviceService,
    VideoChatService,
    MonitoringService,
    ClipboardResponseService,
    ClipboardService,
    TimerService,
    HeaderToastMessageService,
    ToastService,
    ModalSocialEventService,
    SceneService,
    VideoRoomService,
    PlaylistService,
    ConvoService,
    ConvoParticipantService,
    ToasterVideoService,
    PerformerViewService,
    PerformerService,
    PresentationService,
    PresentationRecorderService,
    PresentationAudioService,
    SocialMessageService,
    AdminService,
    CleanUpTasksService,
    BlackSimpleToasterService,
    PermissionsService,
    WebsocketTimerService,
    SocialResolverService,
    ChatService,
    PhotoBoothService,
    ShareScreenService,
    GroupShareScreenService,
    ShareScreenParticipantService,
    GroupShareScreenParticipantService,
    GroupShareScreenRecorderService,
    ActionMenuService,
    RtcStatsService,
    HostService,
    MultimediaRoomService,
    MultimediaUserService,
    HeaderMultipleOptionsMessageService,
    BrowserService,
    SocialPageService,
    ArtifactService,
    ThemeService,
    LivestreamService,
    SubscriptionService,
    RoomLifecycleService,
    RoomPresenceService,
    RoomStateService,
    QAService,
    VideoEffectService,
    CTAService,
    DebuggerService,
    EntryFlowService,
    GalleryService,
    DevicePermissionService,
    ConnectedRoomsService,
    BadgingService,
    CrossRoomParticipantService,
    ClosedCaptionsService,
    EasingService,
    TransitionService,
    TransitionPresentationService,
  ],
  exports: [SocialThemeCarouselComponent, BadgingInfoComponent, AlternateHostsSelectorComponent],
})
export class SocialModule {}
